import { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { useVideoFocus } from "../contexts/videoFocus";
import { black, gray, white } from "../styles/colors";
import { FillImage } from "./FillImage";

type PhoneFrameProps = {
  id?: string;
  posterSrc: string;
  videoSrc?: string;
  alt: string;
  recordingLabel?: string;
};

export function PhoneFrame({
  id: idProp,
  posterSrc,
  videoSrc,
  alt,
  recordingLabel,
}: PhoneFrameProps) {
  const autoId = useId();
  const frameId = idProp ?? autoId;
  const { focusedId, setVisibility } = useVideoFocus();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const backgroundVideoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const isFocused = focusedId === frameId;
  const showVideo = Boolean(videoSrc) && !videoFailed;
  const revealVideo = showVideo && videoReady && isFocused;

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const reportVisibility = (ratio: number) => {
      setVisibility(frameId, ratio);
    };

    const reportFromRect = () => {
      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const visibleHeight =
        Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      reportVisibility(
        Math.max(0, Math.min(1, visibleHeight / (rect.height || 1)))
      );
    };

    reportFromRect();

    const observer = new IntersectionObserver(
      ([entry]) => {
        reportVisibility(entry?.intersectionRatio ?? 0);
      },
      { threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1] }
    );

    observer.observe(node);
    window.addEventListener("scroll", reportFromRect, { passive: true });
    window.addEventListener("resize", reportFromRect);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", reportFromRect);
      window.removeEventListener("resize", reportFromRect);
      setVisibility(frameId, 0);
    };
  }, [frameId, setVisibility]);

  useEffect(() => {
    const video = videoRef.current;
    const backgroundVideo = backgroundVideoRef.current;
    if (!video || !showVideo || !videoReady) return;

    if (isFocused) {
      void video.play().catch(() => {});
      if (backgroundVideo) void backgroundVideo.play().catch(() => {});
      return;
    }

    video.pause();
    video.currentTime = 0;
    if (backgroundVideo) {
      backgroundVideo.pause();
      backgroundVideo.currentTime = 0;
    }
  }, [isFocused, showVideo, videoReady]);

  return (
    <Frame ref={containerRef}>
      <Notch />
      <Screen>
        <MediaLayer $visible={!revealVideo}>
          <FillImage src={posterSrc} alt={alt} $fit="cover" />
        </MediaLayer>
        {showVideo && (
          <MediaLayer $visible={revealVideo} aria-hidden={!revealVideo}>
            <VideoStack>
              <BackgroundVideoWrap aria-hidden>
                <BackgroundVideo
                  ref={backgroundVideoRef}
                  src={videoSrc}
                  preload="auto"
                  loop
                  muted
                  playsInline
                  tabIndex={-1}
                />
              </BackgroundVideoWrap>
              <ForegroundVideo
                ref={videoRef}
                src={videoSrc}
                preload="auto"
                loop
                muted
                playsInline
                onLoadedData={() => setVideoReady(true)}
                onCanPlayThrough={() => setVideoReady(true)}
                onError={() => setVideoFailed(true)}
              />
            </VideoStack>
          </MediaLayer>
        )}
        {showVideo && !videoReady && !videoFailed && (
          <LoadingOverlay aria-hidden="true">
            <Spinner />
          </LoadingOverlay>
        )}
        {recordingLabel && (!showVideo || videoFailed) && (
          <RecordingHint>
            <RecordingDot />
            <span>{recordingLabel}</span>
          </RecordingHint>
        )}
      </Screen>
    </Frame>
  );
}

const PHONE_WIDTH = 280;

const Frame = styled.div({
  position: "relative",
  width: PHONE_WIDTH,
  maxWidth: "100%",
  aspectRatio: "9 / 19.5",
  flexShrink: 0,
  background: black,
  borderRadius: 44,
  padding: 10,
  boxShadow:
    "0 40px 80px rgba(35, 35, 35, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.06) inset",
});

const Notch = styled.div({
  position: "absolute",
  top: 14,
  left: "50%",
  transform: "translateX(-50%)",
  width: 96,
  height: 28,
  background: black,
  borderRadius: 20,
  zIndex: 2,
});

const Screen = styled.div({
  position: "absolute",
  inset: 10,
  borderRadius: 34,
  overflow: "hidden",
  background: black,
});

const MediaLayer = styled.div<{ $visible: boolean }>(({ $visible }) => ({
  position: "absolute",
  inset: 0,
  opacity: $visible ? 1 : 0,
  transition: "opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1)",
  pointerEvents: "none",
}));

const VideoStack = styled.div({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
});

const videoBase = {
  display: "block",
  width: "100%",
  height: "100%",
};

const BackgroundVideoWrap = styled.div({
  position: "absolute",
  inset: "-30%",
  filter: "blur(80px) saturate(1.2)",
});

const BackgroundVideo = styled.video({
  ...videoBase,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "scale(1.1)",
});

const ForegroundVideo = styled.video({
  ...videoBase,
  position: "relative",
  objectFit: "contain",
  zIndex: 1,
});

const LoadingOverlay = styled.div({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0, 0, 0, 0.15)",
  pointerEvents: "none",
  zIndex: 1,
});

const Spinner = styled.div({
  width: 28,
  height: 28,
  border: "2px solid rgba(255,255,255,0.2)",
  borderTopColor: white,
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
});

const RecordingHint = styled.div({
  position: "absolute",
  bottom: 16,
  left: 12,
  right: 12,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 12,
  background: "rgba(35, 35, 35, 0.82)",
  backdropFilter: "blur(8px)",
  color: white,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1.3,
});

const RecordingDot = styled.span({
  flexShrink: 0,
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: gray,
  animation: "pulse-ring 2s ease-in-out infinite",
});
